import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import { inviteStaff, fetchAdminRoles, queryKeys, AdminApiError } from '../lib/api';

const ERROR_COPY: Record<string, string> = {
  UNKNOWN_ROLE: 'That role no longer exists. Reload the page and pick again.',
};

export function StaffNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: roles } = useQuery({
    queryKey: queryKeys.adminRoles,
    queryFn: fetchAdminRoles,
  });

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () => inviteStaff({ email, roleId, ...(displayName ? { displayName } : {}) }),
    onSuccess: async (staff) => {
      setFormError(null);
      await qc.invalidateQueries({ queryKey: queryKeys.staff({}) });
      void navigate({ to: '/staff/$uid', params: { uid: staff.uid } });
    },
    onError: (err) => {
      setFormError(
        err instanceof AdminApiError
          ? (ERROR_COPY[err.code] ?? err.message)
          : 'Could not invite this operator.',
      );
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/staff"
        className="inline-flex items-center gap-1 text-body-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={14} /> Staff
      </Link>

      <div>
        <h1 className="text-heading-2 text-primary">Invite operator</h1>
        {/* No email is sent. apps/admin has no mailer port wired -- a deliberate
            scoping ruling, not an oversight (slice 3 design doc, ruling 6):
            operators authenticate through CIP/Google, and this row is reconciled
            to their real uid on first sign-in. The previous copy said "Sends an
            invitation", which promised a mail that never goes out and left the
            operator waiting on it. */}
        <p className="mt-1 text-body-sm text-muted">
          Grants an existing{' '}
          <span className="text-body-sm-emphasis text-secondary">@heyroot.ai</span> Google account
          the selected role. <span className="text-primary">No email is sent</span> — tell them to
          sign in at this admin site, and their access applies on first sign-in.
        </p>
      </div>

      <form
        className="flex max-w-xl flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}
      >
        {formError && (
          <div
            role="alert"
            className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-3"
          >
            {formError}
          </div>
        )}

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Email</span>
          <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Display name (optional)</span>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Role</span>
          <Select value={roleId} onValueChange={setRoleId}>
            <SelectTrigger aria-label="Role">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {(roles ?? []).map((r) => (
                <SelectItem key={r.roleId} value={r.roleId}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        <div className="mt-2 flex gap-2">
          <Button type="submit" loading={invite.isPending} disabled={!email || !roleId}>
            Invite
          </Button>
          <Button type="button" variant="secondary" onClick={() => void navigate({ to: '/staff' })}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
